'use client';

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PageHero } from '@/components/shared/PageHero';
import { RichText } from '@/components/shared/RichText';
import { useTranslations } from '@/i18n/intl';
import { Link } from '@/components/shared/Link';
import { MapPin, ArrowLeft } from 'lucide-react';
import { parseJsonField } from '@/lib/utils';
import { getJobBySlug } from '@/lib/actions/jobs';
import NotFoundPage from './NotFoundPage';

export default function CareerDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const t = useTranslations('Common');
  const [job, setJob] = useState<any>(null);
  const [notFound, setNotFound] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!slug) return;
    void getJobBySlug(slug).then((data) => {
      if (!data || !data.isActive) {
        setNotFound(true);
      } else {
        setJob(data);
      }
      setReady(true);
    });
  }, [slug]);

  if (!ready) return null;
  if (notFound || !job) return <NotFoundPage />;

  const requirements = parseJsonField<string[]>(job.requirements, []);

  return (
    <>
      <Helmet>
        <title>{`${job.title} | Simisi&Co. Careers`}</title>
        <meta name="description" content={job.shortDescription} />
      </Helmet>
      <PageHero
        title={job.title}
        subtitle={job.location || 'Jakarta, Indonesia'}
        breadcrumbs={[
          { label: t('home'), href: '/' },
          { label: t('career'), href: '/life-at-simisi' },
          { label: job.title },
        ]}
      />

      <section className="py-10 md:py-[72px] px-5 md:px-[40px] bg-white">
        <div className="max-w-[800px] mx-auto">
          <Link
            href="/life-at-simisi"
            className="inline-flex items-center gap-2 text-primary font-semibold mb-8 hover:text-navy transition-colors font-sans"
          >
            <ArrowLeft size={16} />
            Back to Careers
          </Link>

          <div className="flex flex-wrap items-center gap-6 mb-10 pb-10 border-b border-light">
            <div className="flex items-center gap-2 text-gray font-sans">
              <MapPin size={18} className="text-primary" />
              <span>{job.location || 'Jakarta, Indonesia'}</span>
            </div>
          </div>

          <div className="space-y-12">
            <div>
              <h2 className="text-[24px] font-bold text-navy mb-6 font-heading">Description & Responsibilities</h2>
              <RichText
                html={job.description || ''}
                className="text-[16px] text-gray-600 leading-relaxed font-sans [&_p]:mb-4"
              />
            </div>

            {requirements.length > 0 && (
              <div>
                <h2 className="text-[24px] font-bold text-navy mb-6 font-heading">Requirements</h2>
                <ul className="space-y-4">
                  {requirements.map((req: string, index: number) => (
                    <li key={index} className="flex gap-4 items-start">
                      <div className="mt-2.5 shrink-0">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      </div>
                      <p className="text-[16px] text-gray-600 leading-relaxed font-sans">
                        {req}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="pt-8">
              <a
                href={job.applicationLink || `mailto:career@simisi.id?subject=Application for ${job.title}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-primary text-white text-[16px] font-bold uppercase px-12 py-5 rounded-full hover:bg-navy transition-all duration-500 shadow-lg shadow-primary/20"
              >
                Apply for this position
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
